import React, { useState, useEffect } from 'react';
import {
  Box,
  Grid,
  Paper,
  Typography,
  useMediaQuery,
  CircularProgress,
  Card,
  CardContent,
  Chip,
  Avatar,
  Stack,
  Alert,
} from '@mui/material';
import { useNavigate } from 'react-router-dom';
import Sidebar from '../../../components/sidebars/Sidebar_hotelAdmin';
import TopNav from '../../../components/TopNav_hotelAdmin';
import StatisticsCards from '../../../components/StatisticsCard';
import axios from 'axios';
import { 
  Bar, 
 
  Line,
 
} from 'react-chartjs-2';
import {
  Chart as ChartJS,
  CategoryScale,
  LinearScale,
  BarElement,
  Title,
  Tooltip,
  Legend,
  ArcElement,
  LineElement,
  PointElement,
} from 'chart.js';

// Register ChartJS components
ChartJS.register(
  CategoryScale,
  LinearScale,
  BarElement,
  Title,
  Tooltip,
  Legend,
  ArcElement,
  LineElement,
  PointElement
);

// Modern color palette
const CHART_COLORS = {
  primary: '#6366F1',
  secondary: '#EC4899',
  success: '#10B981',
  warning: '#F59E0B',
  info: '#3B82F6',
  purple: '#8B5CF6',
  indigo: '#4F46E5',
  pink: '#EC4899',
  blue: '#3B82F6',
  teal: '#14B8A6',
  orange: '#F97316',
};

const GRADIENT_COLORS = [
  'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
  'linear-gradient(135deg, #f093fb 0%, #f5576c 100%)',
  'linear-gradient(135deg, #4facfe 0%, #00f2fe 100%)',
  'linear-gradient(135deg, #43e97b 0%, #38f9d7 100%)',
  'linear-gradient(135deg, #fa709a 0%, #fee140 100%)',
];

const monthNames = [
  "Jan", "Feb", "Mar", "Apr", "May", "Jun",
  "Jul", "Aug", "Sep", "Oct", "Nov", "Dec"
];

const Dashboard = () => {
  const navigate = useNavigate();
  const [loading, setLoading] = useState(true);
  const [bookingData, setBookingData] = useState(null);
  const [guestData, setGuestData] = useState(null);
  const [roomData, setRoomData] = useState(null);
  const [error, setError] = useState(null);

  // Get hotel code from user context or localStorage - IMPROVED VERSION
  const getHotelCode = () => {
    // Retrieve hotel data from localStorage (set during login)
    const storedProfile = JSON.parse(localStorage.getItem('userProfile')) || {};
    const hotelCode = storedProfile.hotelcode || storedProfile.hotel_code || storedProfile.hotelCode || '';
    
    console.log('Stored Profile:', storedProfile);
    console.log('Extracted Hotel Code:', hotelCode);
    
    if (!hotelCode) {
      console.warn('No hotel code found in user profile, using fallback');
     
    }
    
    return hotelCode;
  };

  const hotel_code = getHotelCode();

  const isMobile = useMediaQuery('(max-width:768px)');
  const [sidebarOpen, setSidebarOpen] = useState(true);

  const toggleSidebar = () => setSidebarOpen((prev) => !prev);

  const handleLogout = () => {
    localStorage.removeItem('authToken');
    localStorage.removeItem('userProfile');
    navigate('/login');
  };

  const handleNotificationClick = () => {
    alert('You have new notifications!');
  };

  // Fetch all analytics data
  useEffect(() => {
    const fetchAnalyticsData = async () => {
      try {
        setLoading(true);
        setError(null);
        
        // Use direct API base URL since we know it works
       const baseURL = process.env.REACT_APP_API_BASE_URL;
        
        console.log('Fetching data for hotel:', hotel_code);

        // Fetch booking analytics
        try {
          const bookingResponse = await axios.get(
            `${baseURL}/hotel/staff/reception/bookings_analytics.php?hotel_code=${hotel_code}`
          );
          console.log('Booking API Response:', bookingResponse.data);
          setBookingData(bookingResponse.data);
        } catch (bookingError) {
          console.error('Error fetching booking data:', bookingError);
          setError('Failed to load booking analytics');
        }

        // Fetch guest analytics
        try {
          const guestResponse = await axios.get(
            `${baseURL}/hotel/staff/reception/guest_analytics.php?hotel_code=${hotel_code}`
          );
          console.log('Guest API Response:', guestResponse.data);
          setGuestData(guestResponse.data);
        } catch (guestError) {
          console.error('Error fetching guest data:', guestError);
          setError('Failed to load guest analytics');
        }

        // Fetch room performance
        try {
          const roomResponse = await axios.get(
            `${baseURL}/hotel/staff/reception/room_performance.php?hotel_code=${hotel_code}`
          );
          console.log('Room API Response:', roomResponse.data);
          setRoomData(roomResponse.data);
        } catch (roomError) {
          console.error('Error fetching room data:', roomError);
         
        }

      } catch (error) {
        console.error('Error fetching analytics data:', error);
        setError('Failed to load dashboard data');
      } finally {
        setLoading(false);
      }
    };

    fetchAnalyticsData();
  }, [hotel_code]);

  // Modern chart options
  const barOptions = {
    responsive: true,
    plugins: {
      legend: {
        position: 'top',
        labels: {
          usePointStyle: true,
          padding: 15,
          font: {
            family: "'Inter', sans-serif",
            size: 12
          }
        }
      },
    },
    scales: {
      x: { 
        grid: { 
          display: false,
          color: 'rgba(0, 0, 0, 0.05)'
        },
        ticks: {
          font: {
            family: "'Inter', sans-serif"
          }
        }
      },
      y: { 
        grid: { 
          color: 'rgba(0, 0, 0, 0.05)' 
        },
        ticks: {
          font: {
            family: "'Inter', sans-serif"
          }
        }
      },
    },
    maintainAspectRatio: false,
  };

  const lineOptions = {
    ...barOptions,
    plugins: {
      legend: {
        position: 'top',
        labels: {
          usePointStyle: true,
          padding: 15,
          font: {
            family: "'Inter', sans-serif",
            size: 12
          }
        }
      },
    },
    scales: {
      x: { 
        grid: { 
          display: false,
          color: 'rgba(0, 0, 0, 0.05)'
        },
        ticks: {
          font: {
            family: "'Inter', sans-serif"
          }
        }
      },
      y: { 
        grid: { 
          color: 'rgba(0, 0, 0, 0.05)' 
        },
        ticks: {
          font: {
            family: "'Inter', sans-serif"
          }
        }
      },
    },
  };

  // Prepare chart data from API responses with better error handling
  const revenueTrendChart = bookingData?.monthly_trend?.length > 0 ? {
    labels: bookingData.monthly_trend.map(item => {
      const [year, month] = item.month.split('-');
      return `${monthNames[parseInt(month) - 1]} ${year}`;
    }),
    datasets: [
      {
        label: 'Monthly Revenue (₦)',
        data: bookingData.monthly_trend.map(item => parseFloat(item.monthly_revenue) || 0),
        backgroundColor: CHART_COLORS.primary,
        borderColor: CHART_COLORS.primary,
        borderWidth: 3,
        fill: true,
        tension: 0.4,
        pointBackgroundColor: CHART_COLORS.primary,
        pointBorderColor: '#ffffff',
        pointBorderWidth: 2,
        pointRadius: 6,
        pointHoverRadius: 8,
      },
    ],
  } : null;

  const roomPerformanceChart = bookingData?.room_performance?.length > 0 ? {
    labels: bookingData.room_performance.map(item => item.room_type || 'Unknown'),
    datasets: [
      {
        label: 'Total Revenue (₦)',
        data: bookingData.room_performance.map(item => parseFloat(item.total_revenue) || 0),
        backgroundColor: CHART_COLORS.success,
        borderColor: CHART_COLORS.success,
        borderWidth: 1,
        borderRadius: 8,
        borderSkipped: false,
      },
      {
        label: 'Booking Count',
        data: bookingData.room_performance.map(item => parseInt(item.bookings_count) || 0),
        backgroundColor: CHART_COLORS.warning,
        borderColor: CHART_COLORS.warning,
        borderWidth: 1,
        borderRadius: 8,
        borderSkipped: false,
      },
    ],
  } : null;

  const highValueGuestsChart = guestData?.high_value_guests?.length > 0 ? {
    labels: guestData.high_value_guests.slice(0, 8).map(item => 
      `${item.first_name || ''} ${item.last_name || ''}`.trim() || 'Unknown Guest'
    ),
    datasets: [
      {
        label: 'Lifetime Value (₦)',
        data: guestData.high_value_guests.slice(0, 8).map(item => parseFloat(item.lifetime_value) || 0),
        backgroundColor: CHART_COLORS.purple,
        borderColor: CHART_COLORS.purple,
        borderWidth: 0,
        borderRadius: 6,
        borderSkipped: false,
      },
    ],
  } : null;

  // Get top repeat guests with error handling
  const topRepeatGuests = guestData?.repeat_guests?.slice(0, 10) || [];

  // Get geographic distribution with error handling
  const geoDistribution = guestData?.geo_distribution?.slice(0, 10) || [];

  // Check if we have any data at all
  const hasData = bookingData || guestData || roomData;

  return (
    <Box sx={{ display: 'flex', minHeight: '100vh', backgroundColor: '#f8fafc' }}>
      {/* Sidebar */}
      <Sidebar isOpen={sidebarOpen} toggleSidebar={toggleSidebar} />

      {/* Main Content */}
      <Box
        sx={{
          flexGrow: 1,
          p: 3,
          ml: isMobile ? 0 : sidebarOpen ? 30 : 5,
          mt: 0,
          maxWidth: '100%',
          mx: 'auto',
        }}
      >
        {/* Top Navigation */}
        <TopNav
          onLogout={handleLogout}
          onNotificationClick={handleNotificationClick}
          toggleSidebar={toggleSidebar}
        />

        {/* Statistics Cards Section */}
        <Box sx={{ mt: 2 }}>
          <StatisticsCards />
        </Box>

        {/* Error Alert */}
        {error && (
          <Alert severity="error" sx={{ mt: 2 }}>
            {error}
          </Alert>
        )}

        {/* Debug Info - Remove in production */}
        <Alert severity="info" sx={{ mt: 2 }}>
          Hotel Code: {hotel_code} | Has Data: {hasData ? 'Yes' : 'No'}
        </Alert>

        {/* Main Charts Grid */}
        <Grid container spacing={3} sx={{ mt: 1 }}>
          
          {/* Revenue Trend Chart */}
          <Grid item xs={12}>
            <Paper 
              elevation={0} 
              sx={{ 
                p: 3, 
                height: 450, 
                borderRadius: 3,
                background: 'white',
                border: '1px solid #e2e8f0',
                boxShadow: '0 4px 6px -1px rgb(0 0 0 / 0.1)',
              }}
            >
              <Typography variant="h5" gutterBottom sx={{ fontWeight: 600, color: '#1e293b', mb: 3 }}>
                📈 Monthly Revenue Trend
              </Typography>
              {loading ? (
                <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: 350 }}>
                  <CircularProgress />
                </Box>
              ) : revenueTrendChart ? (
                <Box sx={{ height: 350 }}>
                  <Line data={revenueTrendChart} options={lineOptions} />
                </Box>
              ) : (
                <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: 350, flexDirection: 'column' }}>
                  <Typography color="text.secondary" gutterBottom>
                    No revenue data available
                  </Typography>
                  <Typography variant="body2" color="text.secondary">
                    Check if hotel code is correct and data exists
                  </Typography>
                </Box>
              )}
            </Paper>
          </Grid>

          {/* Room Performance */}
          <Grid item xs={12}>
            <Paper 
              elevation={0} 
              sx={{ 
                p: 3, 
                height: 450, 
                borderRadius: 3,
                background: 'white',
                border: '1px solid #e2e8f0',
                boxShadow: '0 4px 6px -1px rgb(0 0 0 / 0.1)',
              }}
            >
              <Typography variant="h5" gutterBottom sx={{ fontWeight: 600, color: '#1e293b', mb: 3 }}>
                🏨 Room Performance Analysis
              </Typography>
              {loading ? (
                <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: 350 }}>
                  <CircularProgress />
                </Box>
              ) : roomPerformanceChart ? (
                <Box sx={{ height: 350 }}>
                  <Bar data={roomPerformanceChart} options={barOptions} />
                </Box>
              ) : (
                <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: 350 }}>
                  <Typography color="text.secondary">No room performance data available</Typography>
                </Box>
              )}
            </Paper>
          </Grid>

          {/* High Value Guests */}
          <Grid item xs={12}>
            <Paper 
              elevation={0} 
              sx={{ 
                p: 3, 
                height: 500, 
                borderRadius: 3,
                background: 'white',
                border: '1px solid #e2e8f0',
                boxShadow: '0 4px 6px -1px rgb(0 0 0 / 0.1)',
              }}
            >
              <Typography variant="h5" gutterBottom sx={{ fontWeight: 600, color: '#1e293b', mb: 3 }}>
                ⭐ Top High Value Guests
              </Typography>
              {loading ? (
                <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: 400 }}>
                  <CircularProgress />
                </Box>
              ) : highValueGuestsChart ? (
                <Box sx={{ height: 400 }}>
                  <Bar 
                    data={highValueGuestsChart} 
                    options={{
                      ...barOptions,
                      indexAxis: 'y',
                      plugins: {
                        legend: {
                          display: false
                        }
                      }
                    }}
                  />
                </Box>
              ) : (
                <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: 400 }}>
                  <Typography color="text.secondary">No high value guests data available</Typography>
                </Box>
              )}
            </Paper>
          </Grid>

        </Grid>

        {/* Additional Insights Section */}
        <Grid container spacing={3} sx={{ mt: 1 }}>
          
          {/* Repeat Guests */}
          <Grid item xs={12} md={6}>
            <Paper 
              elevation={0} 
              sx={{ 
                p: 3, 
                borderRadius: 3,
                background: 'white',
                border: '1px solid #e2e8f0',
                boxShadow: '0 4px 6px -1px rgb(0 0 0 / 0.1)',
                minHeight: 500,
              }}
            >
              <Typography variant="h5" gutterBottom sx={{ fontWeight: 600, color: '#1e293b', mb: 3 }}>
                🔄 Top Repeat Guests
              </Typography>
              {loading ? (
                <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: 400 }}>
                  <CircularProgress />
                </Box>
              ) : topRepeatGuests.length > 0 ? (
                <Stack spacing={2}>
                  {topRepeatGuests.map((guest, index) => (
                    <Card 
                      key={index} 
                      elevation={0}
                      sx={{ 
                        border: '1px solid #e2e8f0',
                        borderRadius: 2,
                        transition: 'all 0.2s',
                        '&:hover': {
                          boxShadow: '0 4px 12px rgba(0,0,0,0.1)',
                          transform: 'translateY(-2px)'
                        }
                      }}
                    >
                      <CardContent sx={{ p: 2, '&:last-child': { pb: 2 } }}>
                        <Box sx={{ display: 'flex', alignItems: 'center', mb: 1 }}>
                          <Avatar 
                            sx={{ 
                              width: 40, 
                              height: 40, 
                              mr: 2,
                              bgcolor: GRADIENT_COLORS[index % GRADIENT_COLORS.length]
                            }}
                          >
                            {guest.first_name?.[0]}{guest.last_name?.[0]}
                          </Avatar>
                          <Box sx={{ flexGrow: 1 }}>
                            <Typography variant="subtitle1" fontWeight={600}>
                              {guest.first_name} {guest.last_name}
                            </Typography>
                            <Box sx={{ display: 'flex', gap: 1, mt: 0.5, flexWrap: 'wrap' }}>
                              <Chip 
                                label={`${guest.total_bookings} Bookings`} 
                                size="small" 
                                color="primary" 
                                variant="outlined"
                              />
                              <Chip 
                                label={`₦${parseInt(guest.total_spent || 0).toLocaleString()}`} 
                                size="small" 
                                color="success" 
                                variant="outlined"
                              />
                            </Box>
                          </Box>
                        </Box>
                        <Typography variant="caption" color="text.secondary">
                          Last visit: {guest.last_booking_date ? new Date(guest.last_booking_date).toLocaleDateString() : 'N/A'}
                        </Typography>
                      </CardContent>
                    </Card>
                  ))}
                </Stack>
              ) : (
                <Box sx={{ textAlign: 'center', py: 8 }}>
                  <Typography variant="body2" color="text.secondary">
                    No repeat guests data available
                  </Typography>
                </Box>
              )}
            </Paper>
          </Grid>

          {/* Geographic Distribution */}
          <Grid item xs={12} md={6}>
            <Paper 
              elevation={0} 
              sx={{ 
                p: 3, 
                borderRadius: 3,
                background: 'white',
                border: '1px solid #e2e8f0',
                boxShadow: '0 4px 6px -1px rgb(0 0 0 / 0.1)',
                minHeight: 500,
              }}
            >
              <Typography variant="h5" gutterBottom sx={{ fontWeight: 600, color: '#1e293b', mb: 3 }}>
                🌍 Guest Geographic Distribution
              </Typography>
              
              {loading ? (
                <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: 400 }}>
                  <CircularProgress />
                </Box>
              ) : geoDistribution.length > 0 ? (
                <Stack spacing={2}>
                  {geoDistribution.map((location, index) => (
                    <Card 
                      key={index}
                      elevation={0}
                      sx={{ 
                        border: '1px solid #e2e8f0',
                        borderRadius: 2,
                        background: GRADIENT_COLORS[index % GRADIENT_COLORS.length],
                        color: 'white',
                        transition: 'all 0.2s',
                        '&:hover': {
                          transform: 'translateY(-2px)',
                          boxShadow: '0 6px 20px rgba(0,0,0,0.15)'
                        }
                      }}
                    >
                      <CardContent sx={{ p: 2, '&:last-child': { pb: 2 } }}>
                        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                          <Typography variant="h6" fontWeight={600}>
                            {location.country || location.nationality || 'Unknown Country'}
                          </Typography>
                          <Chip 
                            label={`${location.guest_count || location.count || 0} guests`} 
                            size="small" 
                            sx={{ 
                              backgroundColor: 'rgba(255,255,255,0.2)', 
                              color: 'white',
                              fontWeight: 600
                            }}
                          />
                        </Box>
                      </CardContent>
                    </Card>
                  ))}
                </Stack>
              ) : (
                <Box sx={{ textAlign: 'center', py: 8 }}>
                  <Typography variant="body2" color="text.secondary">
                    No geographic data available
                  </Typography>
                </Box>
              )}
            </Paper>
          </Grid>
        </Grid>
      </Box>
    </Box>
  );
};

export default Dashboard;